import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderArchive, Download, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisYear = new Date().getFullYear();
export const TAX_YEARS = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];

export const downloadTaxPack = async (year, setBusy) => {
  setBusy?.("download");
  try {
    const res = await axios.get(`${API}/reports/tax-pack?year=${year}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `tax-pack-${year}.zip`; a.click(); URL.revokeObjectURL(url);
    toast.success(`Tax pack ${year} downloaded`);
  } catch (e) { toast.error("Could not build the tax pack"); } finally { setBusy?.(null); }
};

export const emailTaxPack = async (year, setBusy, onDone) => {
  if (!window.confirm(`Email the ${year} tax pack ZIP to your accountant now?`)) return;
  setBusy?.("email");
  try {
    const { data } = await axios.post(`${API}/reports/tax-pack/email?year=${year}`, {});
    toast.success(`Tax pack ${year} emailed to ${data.to}`); onDone?.(data);
  } catch (e) { toast.error(e.response?.data?.detail || "Could not email the tax pack"); } finally { setBusy?.(null); }
};

export const TaxPackActions = ({ year, onSent }) => {
  const [busy, setBusy] = useState(null);
  return (
    <div className="flex flex-wrap gap-2" data-testid="tax-pack-actions">
      <Button size="sm" onClick={() => downloadTaxPack(year, setBusy)} disabled={!!busy} className="bg-slate-800 hover:bg-slate-900" data-testid="tax-pack-download"><Download className="w-4 h-4 mr-1" /> {busy === "download" ? "Building…" : `Download Tax Pack ${year}`}</Button>
      <Button size="sm" variant="outline" onClick={() => emailTaxPack(year, setBusy, onSent)} disabled={!!busy} data-testid="tax-pack-email"><Mail className="w-4 h-4 mr-1" /> {busy === "email" ? "Sending…" : "Email to accountant"}</Button>
    </div>
  );
};

export const TaxPackCard = ({ accountantEmail, onSaveEmail }) => {
  const [year, setYear] = useState(thisYear);
  const [s, setS] = useState(null);
  const [email, setEmail] = useState(accountantEmail || "");
  useEffect(() => { setEmail(accountantEmail || ""); }, [accountantEmail]);
  const load = (y) => axios.get(`${API}/reports/tax-pack/summary?year=${y}`).then((r) => setS(r.data)).catch(() => toast.error("Could not load tax pack summary"));
  useEffect(() => { load(year); }, [year]);

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="tax-pack-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div><CardTitle className="flex items-center gap-2"><FolderArchive className="w-5 h-5 text-slate-800" /> Year-End Tax Pack</CardTitle>
          <p className="text-sm text-slate-600 mt-1">One ZIP for your accountant: Schedule C (PDF + CSV), expense detail with receipt references, mileage log, 1099 vendors, P&amp;L, income &amp; invoices, every receipt file, and a README cover sheet with warnings.</p></div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="w-28" data-testid="tax-pack-year"><SelectValue /></SelectTrigger><SelectContent>{TAX_YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
      </CardHeader>
      <CardContent className="space-y-4">
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" data-testid="tax-pack-summary">
            {[["Confirmed expenses", s.expenses], ["Deductible", money(s.deductible)], ["Mileage", `${s.mileage_miles} mi · ${money(s.mileage_deduction)}`], ["Receipts", s.receipts], ["1099 reportable", s.reportable_1099]].map(([l, v]) => <div key={l} className="p-3 rounded-lg bg-slate-50"><p className="text-xs uppercase tracking-wide text-slate-500">{l}</p><p className="font-semibold text-slate-800">{v}</p></div>)}
          </div>
        )}
        {s?.warnings?.length > 0 && <ul className="space-y-1" data-testid="tax-pack-warnings">{s.warnings.map((w, i) => <li key={i} className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w}</li>)}</ul>}
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2"><Label>Accountant email</Label><div className="flex gap-2"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cpa@example.com" data-testid="tax-pack-accountant-email" /><Button size="sm" variant="outline" disabled={email === (accountantEmail || "")} onClick={() => onSaveEmail(email)} data-testid="tax-pack-accountant-save">Save</Button></div></div>
          <div className="text-xs text-slate-500" data-testid="tax-pack-last-sent">{s?.last_sent ? `Last emailed ${s.last_sent.year} pack to ${s.last_sent.to} on ${new Date(s.last_sent.at).toLocaleDateString()}` : "Not emailed yet"}</div>
        </div>
        <TaxPackActions year={year} onSent={() => load(year)} />
        <Badge variant="outline" className="text-slate-500">Confirmed expenses only · receipts named date_vendor_total_id · ZIPs over 20 MB must be downloaded instead of emailed</Badge>
      </CardContent>
    </Card>
  );
};
